import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $cardUsersStore, $usersListStore } from '@lib/store';
import { cn, PaletteUtils, PermissionUtils, RoutePaths } from '@shared/utils';
import { UserType } from '@shared/api';
import { AddUsersModal, RemoveUsersModal, UserItem, View } from '@widgets';
import { Button, Text } from '@shared/widgets';

interface UsersProps {
  className?: string;
  cardId: number;
}

export const Users = (props: UsersProps) => {
  const usersListStore = useUnit($usersListStore.store);
  const cardUsersStore = useUnit($cardUsersStore.store);

  const [showAddUsersModal, setShowAddUsersModal] = useState<boolean>(false);
  const [showRemoveUsersModal, setShowRemoveUsersModal] = useState<boolean>(false);

  const { t } = useTranslation('card');

  useEffect(() => {
    Promise.allSettled([
      $cardUsersStore.events.getUsers({ AssignedCardId: props.cardId }),
      $usersListStore.events.getUsers({ UserType: UserType.User }),
    ]);
    return () => $cardUsersStore.events.clear();
  }, []);

  return (
    <>
      <div className={cn('flex flex-col gap-6', PaletteUtils.isVcfcPalette && 'p-6', props.className)}>
        <div className='flex items-center justify-between gap-4'>
          <Text weight='medium' size='2xl'>
            {t('users.title')}
          </Text>
          {PermissionUtils.manageCardsPermissions() && (
            <div className='flex items-center gap-3'>
              <Button color='outlined' fullWidth onClick={() => setShowAddUsersModal(true)}>
                {t('buttons.add', { ns: 'common' })}
              </Button>
              <Button color='outlined' disabled={!cardUsersStore.data.length} onClick={() => setShowRemoveUsersModal(true)}>
                {t('buttons.remove', { ns: 'common' })}
              </Button>
            </div>
          )}
        </div>
        <View
          loading={cardUsersStore.loading}
          error={cardUsersStore.error}
          onRefresh={() => $cardUsersStore.events.getUsers({ AssignedCardId: props.cardId })}
          noData={!cardUsersStore.data.length}
        >
          <div className='flex flex-wrap gap-4'>
            {cardUsersStore.data.map((manager) => (
              <Link key={manager.id} to={RoutePaths.user.onNavigate(manager.id)}>
                <UserItem letter={manager.firstName.split('')[0]} name={`${manager.firstName} ${manager.lastName}`} />
              </Link>
            ))}
          </div>
        </View>
      </div>
      <AddUsersModal
        show={showAddUsersModal}
        onClose={() => setShowAddUsersModal(false)}
        cardId={props.cardId}
        usersList={usersListStore.data.filter((manager) => !cardUsersStore.data?.map((m) => m.id).includes(manager.id))}
      />
      <RemoveUsersModal
        show={showRemoveUsersModal}
        onClose={() => setShowRemoveUsersModal(false)}
        cardId={props.cardId}
        usersList={cardUsersStore.data}
      />
    </>
  );
};
